import React, { useState, useMemo } from 'react';
import { Bug, Lightbulb } from '@phosphor-icons/react';
import { useContextMenuPlugin } from '@chatsdet/ui';
import { useFeedbackScreenshot } from './useFeedbackScreenshot';
import { FeedbackReportModal } from './FeedbackReportModal';

type FeedbackType = 'bug' | 'feature';

interface FeedbackState {
  isOpen: boolean;
  type: FeedbackType;
  screenshot: string | null;
}

/**
 * Registers "Report Bug" and "Request Feature" plugins in the docs context menu.
 * Only mount this component for superadmin users.
 */
export function FeedbackContextMenuPlugins() {
  const [state, setState] = useState<FeedbackState>({
    isOpen: false,
    type: 'bug',
    screenshot: null,
  });
  const { capture } = useFeedbackScreenshot();

  const openFeedback = async (type: FeedbackType) => {
    const screenshot = await capture();
    setState({ isOpen: true, type, screenshot });
  };

  useContextMenuPlugin(
    useMemo(
      () => ({
        id: 'report-bug',
        label: 'Report Bug',
        icon: <Bug size={16} />,
        onAction: () => {
          openFeedback('bug');
        },
      }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      []
    )
  );

  useContextMenuPlugin(
    useMemo(
      () => ({
        id: 'request-feature',
        label: 'Request Feature',
        icon: <Lightbulb size={16} />,
        onAction: () => {
          openFeedback('feature');
        },
      }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      []
    )
  );

  return (
    <FeedbackReportModal
      isOpen={state.isOpen}
      onOpenChange={(open) => setState((s) => ({ ...s, isOpen: open }))}
      initialType={state.type}
      screenshotDataUrl={state.screenshot}
    />
  );
}
